import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LINKS } from '../constants/links';
import { loadNotifications, unreadCount, startNotificationSync } from '../utils/notifications';
import ConfirmDialog from './ConfirmDialog';
import MeetingNoticeIntro from './MeetingNoticeIntro';
import NotificationsPanel from './NotificationsPanel';
import { useTheme } from '../theme/ThemeContext';
import './styles/AppShell.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${PUBLIC_URL}/logo.png`;
const ISO_CUBE = `${PUBLIC_URL}/iso-cube-clear.png`;
const ISO_PYRAMID = `${PUBLIC_URL}/iso-pyramid-clear.png`;

const GARLAND_BULBS = 18;

const AppShell = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState(() => loadNotifications());
  const [dockPop, setDockPop] = useState('');
  const [dockHint, setDockHint] = useState('');
  const [externalConfirm, setExternalConfirm] = useState(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [headerGlass, setHeaderGlass] = useState(false);
  const notifAnchorRef = useRef(null);
  const dockPopTimer = useRef(null);
  const lastScrollY = useRef(0);

  const pulseDockIcon = (key) => {
    if (dockPopTimer.current) clearTimeout(dockPopTimer.current);
    setDockPop('');
    requestAnimationFrame(() => {
      setDockPop(key);
      dockPopTimer.current = setTimeout(() => setDockPop(''), 580);
    });
  };

  useEffect(() => {
    setShowNotifications(false);
    setHeaderHidden(false);
    setHeaderGlass(false);
    setDockHint('');
    lastScrollY.current = window.scrollY || 0;
  }, [location.pathname]);

  // Telegram-style: вниз — навбар уезжает, вверх — возвращается с glassmorphism
  useEffect(() => {
    if (showNotifications) {
      setHeaderHidden(false);
      return undefined;
    }

    const scrollRoots = [
      window,
      document,
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.getElementById('root'),
    ].filter(Boolean);

    const readY = () => {
      const root = document.getElementById('root');
      return Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement?.scrollTop || 0,
        document.body?.scrollTop || 0,
        root?.scrollTop || 0,
        document.scrollingElement?.scrollTop || 0
      );
    };

    lastScrollY.current = readY();
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = readY();
        const delta = y - lastScrollY.current;

        setHeaderGlass(y > 8);

        if (y <= 12) {
          setHeaderHidden(false);
        } else if (delta > 4) {
          setHeaderHidden(true);
        } else if (delta < -4) {
          setHeaderHidden(false);
        }

        lastScrollY.current = y;
        ticking = false;
      });
    };

    scrollRoots.forEach((target) => {
      target.addEventListener('scroll', onScroll, { passive: true, capture: true });
    });
    // на случай, если скролл идёт внутри оболочки
    const shell = document.querySelector('.app-shell');
    if (shell) shell.addEventListener('scroll', onScroll, { passive: true, capture: true });

    onScroll();

    return () => {
      scrollRoots.forEach((target) => {
        target.removeEventListener('scroll', onScroll, { capture: true });
      });
      if (shell) shell.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [showNotifications, location.pathname]);

  useEffect(() => {
    const path = location.pathname || '';
    if (path.startsWith('/payment') || path.startsWith('/curatorship')) pulseDockIcon('payment');
    else if (path.startsWith('/profile')) pulseDockIcon('profile');
    else if (path.startsWith('/schedule') || path === '/') pulseDockIcon('schedule');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => () => {
    if (dockPopTimer.current) clearTimeout(dockPopTimer.current);
  }, []);

  useEffect(() => {
    const sync = () => setNotifications(loadNotifications());
    window.addEventListener('storage', sync);
    window.addEventListener('kkt-notifications', sync);
    const stop = startNotificationSync((list) => setNotifications(list));
    return () => {
      stop();
      window.removeEventListener('storage', sync);
      window.removeEventListener('kkt-notifications', sync);
    };
  }, []);

  useEffect(() => {
    if (!showNotifications) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') setShowNotifications(false);
    };
    const onPointer = (e) => {
      if (e.target.closest('.notifications-popover, .confirm-dialog, .kkt-blur-backdrop, .seasonal-intro')) return;
      if (notifAnchorRef.current && notifAnchorRef.current.contains(e.target)) return;
      setShowNotifications(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [showNotifications]);

  const badge = unreadCount(notifications);

  const openExternal = useCallback((url, label) => {
    setExternalConfirm({
      url,
      label: label || 'внешний сайт',
    });
  }, []);

  const closeExternalConfirm = useCallback(() => {
    setExternalConfirm(null);
  }, []);

  const confirmExternalGo = useCallback(() => {
    setExternalConfirm(null);
  }, []);

  const isProfile = (location.pathname || '').startsWith('/profile');
  const isPayment = (location.pathname || '').startsWith('/payment');
  const isVkLink = (location.pathname || '').startsWith('/profile/vk');
  const hideChrome = isPayment || isVkLink;
  const staffDock = Boolean(user && user.role && user.role !== 'student');
  const path = location.pathname || '';
  const pathDock = path.startsWith('/profile')
    ? 'profile'
    : path.startsWith('/curatorship')
      ? 'curatorship'
      : path.startsWith('/payment')
        ? 'payment'
        : 'schedule';
  const dockOn = (key) => (dockHint || pathDock) === key;
  const markDock = (key) => {
    pulseDockIcon(key);
    setDockHint(key);
  };
  const dockItemClass = (key, extra = '') =>
    `app-dock__item${extra ? ` ${extra}` : ''} ${dockOn(key) ? 'is-active' : ''} ${
      dockPop === key ? 'is-pop' : ''
    }`;

  return (
    <div
      className={[
        'app-shell',
        isProfile ? 'app-shell--profile' : '',
        isPayment ? 'app-shell--payment' : '',
        isVkLink ? 'app-shell--vk-link' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="app-shell__bg" aria-hidden="true">
        <div className="app-shell__blob app-shell__blob--1" />
        <div className="app-shell__blob app-shell__blob--2" />
        <img className="app-shell__iso app-shell__iso--cube" src={ISO_CUBE} alt="" decoding="async" />
        <img className="app-shell__iso app-shell__iso--pyramid" src={ISO_PYRAMID} alt="" decoding="async" />
        <img className="app-shell__iso app-shell__iso--cube-sm" src={ISO_CUBE} alt="" decoding="async" />
      </div>

      {!hideChrome ? (
      <header
        className={[
          'app-shell__header',
          showNotifications ? 'is-notif-open' : '',
          headerGlass ? 'is-glass' : '',
          headerHidden ? 'is-hidden' : '',
          isProfile ? 'is-profile-page' : '',
          theme === 'snow' ? 'has-garland' : '',
        ].filter(Boolean).join(' ')}
      >
        {theme === 'snow' ? (
          <div className="nav-garland" aria-hidden="true">
            <svg className="nav-garland__wire" viewBox="0 0 100 10" preserveAspectRatio="none">
              <path d="M0 3 Q 12.5 8 25 3 T 50 3 T 75 3 T 100 3" fill="none" stroke="currentColor" strokeWidth="0.35" />
            </svg>
            <div className="nav-garland__bulbs">
              {Array.from({ length: GARLAND_BULBS }, (_, i) => (
                <span
                  key={i}
                  className={`nav-garland__bulb nav-garland__bulb--${i % 5}`}
                  style={{ animationDelay: `${(i % 7) * 0.18}s` }}
                />
              ))}
            </div>
          </div>
        ) : null}
        <div className="app-shell__header-inner">
          <button
            type="button"
            className="app-shell__brand"
            onClick={() => navigate('/schedule')}
          >
            <img src={LOGO_SRC} alt="Логотип" className="app-shell__logo" width={40} height={40} decoding="async" fetchPriority="high" />
            <div className="app-shell__brand-text">
              <span className="app-shell__title">Расписание</span>
              <span className="app-shell__tagline">Краснодарский кооперативный техникум</span>
            </div>
          </button>

          <div className="app-shell__notif-anchor" ref={notifAnchorRef}>
            <button
              type="button"
              className={`app-shell__bell ${showNotifications ? 'is-open' : ''}`}
              onClick={() => setShowNotifications((v) => !v)}
              aria-label="Уведомления"
              aria-expanded={showNotifications}
              aria-haspopup="dialog"
              title="Уведомления"
            >
              <i className="bi bi-bell"></i>
              {badge > 0 && <span className="app-shell__badge">{badge > 9 ? '9+' : badge}</span>}
            </button>

            <NotificationsPanel
              open={showNotifications}
              onClose={() => setShowNotifications(false)}
              notifications={notifications}
              setNotifications={setNotifications}
              anchorRef={notifAnchorRef}
            />
          </div>
        </div>
      </header>
      ) : null}

      <div className="app-shell__content">
        <Outlet
          context={{
            user,
            onLogout,
            onUserUpdate,
            openNotifications: () => setShowNotifications(true),
            notifications,
            setNotifications,
            openExternal,
          }}
        />
      </div>

      {!hideChrome ? (
      <nav className="app-dock" aria-label="Главное меню">
        <div className="app-dock__bar">
          <NavLink
            to="/schedule"
            end
            className={() => dockItemClass('schedule', 'app-dock__item--schedule')}
            onClick={() => markDock('schedule')}
          >
            <i className="bi bi-calendar3"></i>
            <span>Расписание</span>
          </NavLink>

          <button
            type="button"
            className={`app-dock__item app-dock__item--eo ${dockPop === 'eo' ? 'is-pop' : ''}`}
            onClick={() => {
              pulseDockIcon('eo');
              openExternal(LINKS.eo, 'электронное обучение');
            }}
          >
            <i className="bi bi-laptop"></i>
            <span>Электронное обучение</span>
          </button>

          {staffDock ? (
            <NavLink
              to="/curatorship"
              className={() => dockItemClass('curatorship', 'app-dock__item--curatorship')}
              onClick={() => markDock('curatorship')}
            >
              <i className="bi bi-people"></i>
              <span>Куратор</span>
            </NavLink>
          ) : (
            <NavLink
              to="/payment"
              className={() => dockItemClass('payment')}
              onClick={() => markDock('payment')}
            >
              <i className="bi bi-credit-card"></i>
              <span>Оплата</span>
            </NavLink>
          )}

          <NavLink
            to="/profile"
            className={() => dockItemClass('profile')}
            onClick={() => markDock('profile')}
          >
            <i className="bi bi-person"></i>
            <span>Профиль</span>
          </NavLink>
        </div>

        <button
          type="button"
          className={`app-dock__settings ${dockPop === 'settings' ? 'is-pop' : ''} ${location.pathname.startsWith('/schedule') ? 'is-on-schedule' : ''}`}
          onClick={() => {
            pulseDockIcon('settings');
            const open = () => window.dispatchEvent(new CustomEvent('kkt-open-settings'));
            if (!location.pathname.startsWith('/schedule')) {
              navigate('/schedule');
              window.setTimeout(open, 120);
            } else {
              open();
            }
          }}
          aria-label="Настройки расписания"
          title="Настройки расписания"
        >
          <i className="bi bi-sliders"></i>
        </button>
      </nav>
      ) : null}

      <ConfirmDialog
        open={Boolean(externalConfirm)}
        title="Переход на другой сайт"
        href={externalConfirm?.url || ''}
        message={
          externalConfirm
            ? `Вы переходите на другой сайт (${externalConfirm.label}). Продолжить?`
            : ''
        }
        confirmLabel="Перейти"
        cancelLabel="Отмена"
        onConfirm={confirmExternalGo}
        onCancel={closeExternalConfirm}
      />
      <MeetingNoticeIntro
        notifications={notifications}
        setNotifications={setNotifications}
      />
    </div>
  );
};

export default AppShell;
